// Host-local save bridge for the unified instance system.
//
// Only the host of a multiplayer instance owns a save blob. Guests
// see the instance in their save list via server metadata but have
// no local state to load. Solo instances are always "host" for this
// purpose.
//
// This module is a thin bridge between the existing local save
// path (sessionStorage.js) and the new server metadata API
// (instancesApi.js).
//
// Spec: docs/superpowers/specs/2026-04-24-unified-instance-save-system-design.md

import {
  saveGameSession, loadGameSession, loadSessionByInstance, deleteGameSession,
} from './sessionStorage';
import { pauseInstance } from '../arena/instancesApi';

// Save the current match state and mark the instance paused on the
// server. Use this for ESC → Save-and-Exit. Returns the saved local
// blob record (with its generated id) for host paths; returns null
// for guests, where we only pause the server-side metadata.
export async function saveInstanceLocally({
  instanceId, mode, visibility, sessionBlob, isHost,
}) {
  if (!isHost) {
    await pauseInstance(instanceId);
    return null;
  }
  const payload = {
    ...sessionBlob,
    instanceId,
    mode,
    visibility,
    savedAt: new Date().toISOString(),
  };
  const saved = await saveGameSession(payload);
  await pauseInstance(instanceId);
  return saved;
}

// Silent auto-save: write the blob but do NOT flip the instance
// into 'paused' on the server. Used on turn boundaries so a crash
// has a recent snapshot without marking the instance paused while
// play continues.
export async function autoSaveInstanceBlob({
  instanceId, mode, visibility, sessionBlob,
}) {
  const payload = {
    ...sessionBlob,
    instanceId,
    mode,
    visibility,
    savedAt: new Date().toISOString(),
  };
  return saveGameSession(payload);
}

export async function loadInstanceLocally(instanceId) {
  const stub = await loadSessionByInstance(instanceId);
  if (!stub) return null;
  return loadGameSession(stub.id);
}

export async function clearInstanceLocalBlob(instanceId) {
  const existing = await loadSessionByInstance(instanceId);
  if (existing) await deleteGameSession(existing.id);
}

// --- Draft-specific wrappers ---
//
// Drafts use a single save file per tournament that carries the
// full state. Pick-and-pass, deckbuilding, round pairings,
// standings, reward distribution. See draftSaveState.js for the
// schema. These wrappers keep draft persistence using the same
// infrastructure as matches so Phase 7 integration can start
// without bespoke storage.

export async function saveDraftInstance({
  instanceId, visibility, draftState, isHost, pauseAfter = true,
}) {
  if (!isHost) {
    if (pauseAfter) await pauseInstance(instanceId);
    return null;
  }
  const payload = {
    instanceId,
    mode: 'draft',
    visibility,
    draftState,
    savedAt: new Date().toISOString(),
  };
  const saved = await saveGameSession(payload);
  if (pauseAfter) await pauseInstance(instanceId);
  return saved;
}

// Silent auto-save of the draft state. No server pause, no UI
// toast. Expected to be called after every pick, at the end of
// each round, and on other state-transition checkpoints so a
// crash never loses more than the last pick.
export async function autoSaveDraftInstance({
  instanceId, visibility, draftState,
}) {
  const payload = {
    instanceId,
    mode: 'draft',
    visibility,
    draftState,
    savedAt: new Date().toISOString(),
  };
  return saveGameSession(payload);
}

// Loads the draft state associated with an instance id. Returns
// the raw draftState object (or null if no blob exists). The
// wrapping session blob's other fields are not surfaced. Callers
// only care about the tournament state.
export async function loadDraftInstance(instanceId) {
  const full = await loadInstanceLocally(instanceId);
  if (!full) return null;
  return full.draftState || null;
}
